import { Configuration, SuppliersApi } from '@/api';
import useGetStoreQuery from '@/api/queries/useGetStoreQuery';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import StoreForm from '@/modules/supplier/components/StoreForm';
import { StoreAddOrUpdate } from '@/modules/supplier/models/Store';
import { CustomerStoreListPath } from '@/modules/supplier/paths';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const StoreEdit = () => {
  const navigate = useNavigate();

  const { currentUser } = useAuth();
  const { storeId } = useParams();
  const supplierId = currentUser()?.supplierId;

  const { isLoading, isFetching, store, invalidateQuery } = useGetStoreQuery(storeId, supplierId);

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const addStoreToSupplier = async (store: StoreAddOrUpdate, supplierId: string) => {
    await suppliersApi.addStoresToSupplier(supplierId, [
      {
        id: store.id,
        customerCode: store.customerCode,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Store added successfully!" visible={t.visible} />
    ));
  };

  const removeStoreFromSupplier = async (store: StoreAddOrUpdate, supplierId: string) => {
    await suppliersApi.removeStoresFromSupplier(supplierId, [
      {
        id: store.id,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Store removed successfully!" visible={t.visible} />
    ));
  };

  const handleSubmit = async (store: StoreAddOrUpdate) => {
    if (!supplierId || !store) return;

    const shouldAddStore = store.customerCode !== '';

    try {
      if (shouldAddStore) {
        await addStoreToSupplier(store, supplierId);
      } else {
        await removeStoreFromSupplier(store, supplierId);
      }

      await invalidateQuery();

      navigate(CustomerStoreListPath.withCustomerId(store.retailerId));
    } catch (error) {
      console.error('Error while updating store:', error);
    }
  };

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!store) {
    return;
  }

  return (
    <>
      <SettingsHeader title="Edit Store" icon={<BuildingStorefrontIcon className="w-6 h-6" />} />

      <StoreForm store={store} onSubmit={handleSubmit} />
    </>
  );
};

export default StoreEdit;
